<template>
  <v-container mt-7>
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-row>
        <v-col class="selectorColumn">
          <v-select
            v-model="orgChoice"
            :items="organizations"
            :menu-props="{ maxHeight: '400' }"
            label="Organizations"
            :disabled="orgDisabled"
            outlined
            dense
            clearable
          >
            <template v-slot:selection="{ item, index }">
              <span>{{ item }}</span>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ orgChoice.length - 1 }}</span
              >
            </template>
          </v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-select
            v-model="insChoice"
            :items="insurances"
            item-text="Insurance"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select Insurance"
            clearable
            outlined
            dense
            required
            :rules="[v => !!(this.insChoice && (Array.isArray(this.insChoice) ? this.insChoice.length > 0 : this.insChoice)) || 'Insurance is required']"
            @input="onInsuranceChange"
            @click:clear="$nextTick(() => (insChoice = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn" v-if="!groupDisabled">
          <v-autocomplete
            v-model="groupChoice"
            :items="groups"
            item-text="GROUP_NAME"
            item-value="BILLING_PROVIDER_NPI"
            :menu-props="{ maxHeight: '400' }"
            label="All Groups"
            :disabled="groupDisabled"
            multiple
            outlined
            dense
            clearable

            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">{{ item.GROUP_NAME }}</div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ groupChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn" v-if="!provDisabled">
          <v-autocomplete
            v-model="provChoice"
            :items="providers"
            item-value="PROVIDER_ID"
            item-text="PROVIDER_NAME"
            :menu-props="{ maxHeight: '400' }"
            label="All Providers"
            multiple
            clearable
            outlined
            dense
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_NAME }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ provChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>
        <v-col class="selectorColumn" v-if="!locDisabled">
          <v-autocomplete
            v-model="locChoice"
            :items="locations"
            item-text="PROVIDER_LOCATION"
            item-value="PRACTICE_IDs"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Locations"
            multiple
            outlined
            dense
            clearable
            class="newSelect"
          >
            <template v-slot:selection="{ item, index }">
              <div class="selection-text" v-if="index === 0">
                {{ item.PROVIDER_LOCATION || 'No Location Provided' }}
              </div>
              <span v-if="index === 1" class="grey--text caption"
                >+{{ locChoice.length - 1 }}</span
              >
            </template>
          </v-autocomplete>
        </v-col>

        <v-col class="selectorColumn">
          <v-select
            v-model="reportChoice"
            :items="visibleReports"
            :rules="[this.reportChoice.length !== 0 || 'Report is required']"
            label="Select Report"
            required
            item-text="Report Type"
            return-object
            :menu-props="{ maxHeight: '400' }"
            clearable
            outlined
            dense
            @click:clear="$nextTick(() => (reportChoice = []))"
          ></v-select>
          <v-alert
            v-if="reportChoice === 'Patients Not Seen'"
            dense
            :colored-border="true"
            border="bottom"
            color="info"
          >
            This report will return a list of patients who haven't had an appointment 6
            months prior to the month selected.
          </v-alert>
        </v-col>
        <v-col class="selectorColumn">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="startDate"
                :label="startDateText"
                append-icon="mdi-calendar"
                outlined
                dense
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              :max="maxEndDate"
              show-current="false"
              type="month"
              @input="menu = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="selectorColumn" v-if="reportChoice !== 'Patients Not Seen'">
          <v-menu
            ref="menu2"
            v-model="menu2"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                append-icon="mdi-calendar"
                :disabled="endDateDisabled"
                outlined
                dense
                readonly
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              :max="maxEndDate"
              show-current="false"
              type="month"
              @input="menu2 = false"
              no-title
              scrollable
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col v-if="reportChoice === 'Open Tasks'" class="selectorColumn">
          <v-select
            v-model="taskResultType"
            :items="taskResults"
            item-text="Task Result"
            return-object
            :menu-props="{ maxHeight: '400' }"
            label="Select a task"
            clearable
            outlined
            dense
            @click:clear="$nextTick(() => (taskResultType = []))"
          ></v-select>
        </v-col>
        <v-col class="selectorColumn">
          <v-btn @click="validate" color="secondary" depressed block class="py-5">
            Run Report
            <v-icon small>mdi-play</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>

    <v-row>
      <v-col v-if="load" cols="12" class="text-center">
        <v-progress-circular
          color="secondary"
          indeterminate
          :size="50"
          :width="3"
        ></v-progress-circular>
      </v-col>

      <v-col
        v-else-if="(insChoice.length === 0 || reportChoice.length === 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Please select all required fields </v-alert>
      </v-col>

      <v-col
        v-else-if="(insChoice.length > 0 || reportChoice.length > 0) && !showReport"
        cols="12"
      >
        <v-alert :value="true" type="info"> Press run report to continue </v-alert>
      </v-col>

      <v-col v-else-if="showReport && changeDetected" cols="12">
        <v-alert :value="true" type="info">
          Change detected: Run the report to see updated data
        </v-alert>
      </v-col>

      <v-col v-else-if="reportData.length === 0 && showReport" cols="12">
        <v-alert :value="true" type="info">
          There is no data available for this selection. Please change your filter
          selections and run report again
        </v-alert>
      </v-col>

      <MembershipReports
        v-show="reportData.length > 0 && showReport"
        :reportData="reportData"
        :csvData="csvData"
        :reportType="chosenReport ? chosenReport : null"
        :insurance="selectedInsurance.length > 0 ? selectedInsurance : null"
        :startDate="startDate"
        :endDate="endDate"
      />

      <v-snackbar v-model="supportedReport" top timeout="5000" color="primary">
        <v-icon>mdi-information</v-icon>
        Report not supported by insurance selection, please select a different report
      </v-snackbar>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import { debounce } from 'lodash';
import MembershipReports from '../components/membershipComponents/MembershipReports.vue';

export default {
  components: {
    MembershipReports,
  },
  data() {
    return {
      startDate: new Date().toISOString().substr(0, 7),
      endDate: new Date().toISOString().substr(0, 7),
      maxEndDate: new Date(new Date().getFullYear(), new Date().getMonth())
        .toISOString()
        .substr(0, 7),
      menu: false,
      modal: false,
      menu2: false,
      tab: null,
      user: '',
      load: false,
      orgChoice: [],
      groupChoice: [],
      provChoice: [],
      provNums: [],
      locChoice: [],
      insChoice: [],
      reportChoice: [],
      organizations: [],
      providers: [],
      groups: [],
      groupObject: [],
      groupNum: [],
      insurances: [],
      locations: [],
      reportData: [],
      csvData: [],
      defaultReports: [
        'Disenrollments',
        'Membership Roster',
        'Membership Summary',
        'New Members',
        'Patients Not Seen',
      ],
      limitedReports: ['Capitation', 'Membership Roster'],
      supportedReport: false,
      showReport: false,
      changeDetected: false,
      chosenReport: '',
      valid: true,
      selectedInsurance: [],
      taskResultType: false,
      taskResults: [],
    };
  },
  mounted() {
    this.getUser();
  },
  methods: {
    validate() {
      if (this.$refs.form.validate()) {
        this.getReport();
      }
    },
    async getUser() {
      await axios
        .get('/api/user')
        .then((response) => {
          this.user = response.data[0];
        })
        .catch(err => this.$router.push('/login'));
      this.checkPresets();
    },
    checkPresets() {
      if (
        this.user.role === 'Provider'
        || this.user.role === 'Group'
        || this.user.role === 'Affiliation'
      ) {
        this.setPresets();
      } else if (this.user.role === 'Member') {
        this.$router.push('/member');
      } else {
        this.getOrgs();
        this.getInsurances();
      }
      // SEARCH FOR TASKS
      axios
        .get(
          'api/tasks/taskResultList',
        )
        .then((response) => {
          console.log('antes');
          console.log(response);
          console.log('depois');

          response.data.forEach((result) => {
            console.log(result);
            this.taskResults.push(result.result);
          });
        });
    },
    async setPresets() {
      if (this.user.role === 'Group' || this.user.role === 'Affiliation') {
        this.organizations = [this.user.organization];
        this.insurances = this.user.insurance;
      } else {
        this.organizations.push(this.user.organization);
      }
      // this.groups = this.user.group;
      // this.insurances = this.user.insurance;

      if (this.user.role === 'Provider' || this.user.role === 'Specialist') {
        this.orgChoice = this.user.organization;
        this.insChoice = this.user.insurance[0];
        
        // Limpar arrays com strings vazias
        this.groupChoice = (this.user.group && this.user.group.filter(g => g && g.trim() !== '')) || [];
        
        await this.getProvider(this.user.npi);
        
        console.log('🔍 Membership - Provider data loaded:');
        console.log('  - providers:', this.providers);
        console.log('  - groupChoice (cleaned):', this.groupChoice);
        console.log('  - Is array?', Array.isArray(this.providers));
        
        if (!(Array.isArray(this.providers))) {
          // Converter objeto único para array
          const providerObj = this.providers;
          this.providers = [providerObj];
          this.provChoice = [providerObj.PROVIDER_ID];
          console.log('  - Converted to array:', this.providers);
          console.log('  - provChoice:', this.provChoice);
        }
      } else if (this.user.role === 'Group' || this.user.role === 'Affiliation') {
        this.orgChoice = this.user.organization;
        this.insChoice = (this.user.insurance && this.user.insurance[0]) || null;
        // Affiliation/Group: getGroups usa ipaId (não depende de insChoice) — chamar já em setPresets
        await this.getGroups();
      }
    },
    async getReport() {
      this.selectedInsurance = this.insChoice;
      this.load = true;
      this.showReport = true;
      this.changeDetected = false;
      this.supportedReport = false;
      this.reportData = [];
      this.averageObject = {};

      this.chosenReport = this.reportChoice;

      const { orgChoice } = this;
      const { insChoice } = this;
      const { locChoice } = this;
      const { startDate } = this;
      const { endDate } = this;
      const { reportChoice } = this;
      let { taskResultType } = this;
      const { ipaAffiliation } = this;
      const providers = this.provChoice;

      if (taskResultType.length === 0) {
        taskResultType = false;
      }
      let locations = [];
      if (locChoice.length > 0) {
        locChoice.forEach((loca) => {
          if (locations.indexOf(loca.PRACTICE_IDs) === -1) {
            locations = locations.concat(loca.PRACTICE_IDs);
          }
        });
      } else {
        locations = [];
      }
      const insForUrl = typeof insChoice === 'string' ? insChoice : (insChoice && insChoice.Insurance ? insChoice.Insurance : (Array.isArray(insChoice) && insChoice[0] ? (typeof insChoice[0] === 'string' ? insChoice[0] : insChoice[0].Insurance) : insChoice));
      // Rota espera: .../:patient?/:taskResult/:ins/:start/:end — incluir segmento vazio para patient
      const membershipUrl = `api/membership/${reportChoice}/orgs=${orgChoice}/groups=${this.groupChoice}/providers=${providers}/locs=${locations}//${taskResultType}/${insForUrl}/${startDate}/${endDate}/`;
      
      console.log('🔍 Membership getReport called:');
      console.log('  - URL:', membershipUrl);
      console.log('  - reportChoice:', reportChoice);
      console.log('  - providers:', providers);
      console.log('  - groupChoice:', this.groupChoice);
      console.log('  - locations:', locations);
      
      axios
        .get(membershipUrl)
        .then((response) => {
          console.log('✅ Membership API response:', response.data);
          console.log('  - data.length:', response.data.data ? response.data.data.length : 0);
          
          this.averageObject = response.data.averageObj;
          this.reportData = response.data.data;
          this.csvData = JSON.parse(JSON.stringify(this.reportData));
          this.csvData.forEach((obj) => {
            delete obj._id;
            delete obj.ID;
          });
          this.load = false;
          this.cancelSource = null;
        })
        .catch((error) => {
          console.error('❌ Membership API error:', error);
          console.error('❌ Error response:', error.response);
          this.load = false;
        });
    },
    async getOrgs() {
      let customer = (this.user && this.user.customer) || process.env.VUE_APP_CUSTOMER || '';
      if (!customer || !customer.toLowerCase().includes('familyhealthchoice')) {
        try {
          const configRes = await axios.get('api/config');
          customer = (configRes.data && configRes.data.customer) || customer;
        } catch (e) { /* ignora */ }
      }
      const isFHC = () => (customer || '').toLowerCase().includes('familyhealthchoice');
      axios.get('api/list/organization').then((response) => {
        const data = response.data;
        this.organizations = Array.isArray(data) ? data : (data ? [data] : []);
        this.orgChoice = this.organizations[0];
        if (this.organizations[0] === 'medhealthteam') {
          this.defaultReports.push('Open Conditions');
          this.defaultReports.push('Open Tasks');
        }
        // Fallback FHC: se API retornou vazio, usar org padrão
        if (this.organizations.length === 0 && isFHC()) {
          this.organizations = ['FamilyHealthChoice'];
          this.orgChoice = 'FamilyHealthChoice';
        }
      }).catch(() => {
        if (isFHC()) {
          this.organizations = ['FamilyHealthChoice'];
          this.orgChoice = 'FamilyHealthChoice';
        }
      });
    },
    async getLocations() {
      await axios.get(`api/selection/${this.provChoice}/locations`).then((response) => {
        this.locations = (response.data || []).map((loc) => ({
          ...loc,
          PROVIDER_LOCATION: loc.PROVIDER_LOCATION != null ? loc.PROVIDER_LOCATION : 'No Location Provided'
        }));
      });
    },
    async getProviders() {
      if (['provider', 'specialist'].includes(this.userRoleLower)) return;
      const isManagerRole = this.user && ['admin', 'superuser', 'manager'].includes(this.userRoleLower);
      const hasNoGroups = !this.groupChoice || (Array.isArray(this.groupChoice) && this.groupChoice.length === 0);
      if (isManagerRole && hasNoGroups) {
        const ins = (this.insChoice && this.insChoice.Insurance) || (typeof this.insChoice === 'string' ? this.insChoice : 'all');
        const res = await axios.get(`api/providers/getall/${ins}?limit=500`).then(r => r.data).catch(() => ({ data: [] }));
        const list = (res.data || res) || [];
        this.providers = list.map(p => ({ PROVIDER_ID: p.PROVIDER_ID, PROVIDER_NAME: p.PROVIDER_NAME }));
      } else if (this.groupChoice && this.groupChoice.length > 0) {
        await axios.get(`api/selection/${this.groupChoice}/providers`).then((response) => {
          this.providers = response.data;
        });
      } else {
        this.providers = [];
      }
    },
    async getProvider(npi) {
      console.log('🔍 getProvider called with NPI:', npi);
      try {
        const response = await axios.get(`api/selection/single/${npi}/provider`);
        console.log('✅ API response:', response.data);
        this.providers = response.data;
      } catch (error) {
        console.error('❌ Error getting provider:', error);
        console.error('❌ Error response:', error.response);
        // Fallback: Provider/Specialist sempre deve ver a si mesmo na lista
        const u = this.user;
        if (u && (u.role === 'Provider' || u.role === 'Specialist') && (String(u.npi || '') === String(npi))) {
          const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || 'Provider';
          this.providers = [{ PROVIDER_ID: String(npi), PROVIDER_NAME: name }];
          this.provChoice = [String(npi)];
        } else {
          this.providers = [];
          this.provChoice = [];
        }
      }
    },
    async getInsurances() {
      // Resolver customer: user.customer (servidor) > api/config > env client > user.insurance
      let customer = (this.user && this.user.customer) || process.env.VUE_APP_CUSTOMER || '';
      if (!customer || !customer.toLowerCase().includes('familyhealthchoice')) {
        try {
          const configRes = await axios.get('api/config');
          customer = (configRes.data && configRes.data.customer) || customer;
        } catch (e) { /* ignora */ }
      }
      const isFHC = () => {
        if ((customer || '').toLowerCase().includes('familyhealthchoice')) return true;
        if (this.user && this.user.insurance && this.user.insurance.some(ins =>
          String(ins).toLowerCase().includes('familyhealthchoice') || String(ins).toLowerCase().includes('floridahealthchoice')
        )) return true;
        return false;
      };
      const setFHC = () => {
        this.insurances = [{ Insurance: 'FamilyHealthChoice' }];
        this.insChoice = { Insurance: 'FamilyHealthChoice' };
        this.getGroups();
      };
      if (isFHC()) {
        setFHC();
        return;
      }
      axios.get('api/list/insurances').then(async (response) => {
        const data = response.data;
        this.insurances = Array.isArray(data)
          ? data.map(i => (typeof i === 'string' ? { Insurance: i } : (i && i.Insurance ? i : { Insurance: String(i) })))
          : [];
        if (this.insurances.length === 0 && isFHC()) setFHC();
        // Admin/Manager/Superuser: selecionar insurance e carregar grupos
        if (['admin', 'superuser', 'manager'].includes(this.userRoleLower)) {
          if (this.insurances.length === 1) {
            this.insChoice = this.insurances[0];
          }
          await this.getGroups();
        }
      }).catch(() => {
        if (isFHC()) setFHC();
      });
    },
    async getGroups() {
      if (!this.user || !this.user.role) return;
      const r = this.userRoleLower;

      // Assigns entire user.group array if only one insurance, the else if is there in case there is more than one insurance and can only access certain groups within each insurance
      if (
        ((r === 'group' && Number(this.user.npi) == this.user.npi) || r === 'provider')
        && this.insurances.length === 1
      ) {
        await axios.get(`api/list/npi/${this.user.npi}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (r === 'affiliation' || r === 'group') {
        await axios.get(`api/list/ipaId/${this.user.npi}/groups`).then((response) => {
          this.groups = (response.data || []).map((g) => ({
            GROUP_NAME: typeof g.GROUP_NAME === 'string' ? g.GROUP_NAME : (g.GROUP_NAME != null ? String(g.GROUP_NAME) : ''),
            BILLING_PROVIDER_NPI: Array.isArray(g.BILLING_PROVIDER_NPI)
              ? g.BILLING_PROVIDER_NPI.map((x) => String(x)).join(',')
              : (g.BILLING_PROVIDER_NPI != null ? String(g.BILLING_PROVIDER_NPI) : '')
          }));
        });
      } else if (r === 'group' && this.insurances.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          const userObjGroups = this.user.group;
          const allPossibleGroups = response.data;
          this.groups = [];
          allPossibleGroups.forEach((grp) => {
            if (userObjGroups.indexOf(grp) !== -1) {
              this.groups.push(grp);
            }
          });
        });
      } else if (this.insChoice.length > 1) {
        await axios.get(`api/list/${this.insChoice}/groups`).then((response) => {
          this.groups = response.data;
        });
      } else if (['admin', 'superuser', 'manager'].includes(this.userRoleLower)) {
        // Mesmo comportamento do Affiliation: grupos filtrados pelo insurance selecionado
        const ins = Array.isArray(this.insChoice) ? this.insChoice[0] : this.insChoice;
        const insuranceName = ins ? (typeof ins === 'string' ? ins : (ins.Insurance || ins.Names || ins)) : null;
        if (insuranceName) {
          await axios.get(`api/list/${insuranceName}/groups`).then((response) => {
            const data = response.data || [];
            this.groups = data.map((g) => ({
              GROUP_NAME: typeof g.GROUP_NAME === 'string' ? g.GROUP_NAME : (g.GROUP_NAME != null ? String(g.GROUP_NAME) : ''),
              BILLING_PROVIDER_NPI: Array.isArray(g.BILLING_PROVIDER_NPI)
                ? g.BILLING_PROVIDER_NPI.map((x) => String(x)).join(',')
                : (g.BILLING_PROVIDER_NPI != null ? String(g.BILLING_PROVIDER_NPI) : '')
            }));
          }).catch(() => {
            this.groups = [];
          });
        } else {
          this.groups = [];
        }
        this.getProviders();
      }
    },
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel('Start new search, stop active search');
      }
    },
    onInsuranceChange() {
      if (!this.user || !this.user.role) return;
      this.groupChoice = [];
      this.provChoice = [];
      this.locChoice = [];
      this.locations = [];
      const r = this.userRoleLower;
      if (['admin', 'superuser', 'manager', 'group', 'affiliation'].includes(r)) {
        const hasSelection = this.insChoice && (Array.isArray(this.insChoice) ? this.insChoice.length > 0 : !!this.insChoice);
        if (hasSelection) {
          this.getGroups();
        } else {
          this.groups = [];
          this.providers = [];
        }
      }
      if (this.showReport) this.changeDetected = true;
    },
  },
  watch: {
    insChoice() {
      if (!this.user || !this.user.role) return;
      // Admin/Manager/Superuser/Group/Affiliation: onInsuranceChange (@input) chama getGroups
      // Provider/Clinical: watcher define groupChoice
      if (this.userRoleLower === 'provider' || this.userRoleLower === 'clinical') {
        this.insurances = this.user.insurance || this.insurances;
        this.groupChoice = (this.user.group && this.user.group.filter(g => g && g.trim() !== '')) || [];
      }
      if (this.showReport) this.changeDetected = true;
    },
    groupChoice: debounce(async function groupChoice(newValue) {
      if (this.showReport) {
        this.changeDetected = true;
      }
      this.getProviders();
    }, 300),

    provChoice(newValue) {
      if (this.showReport) {
        this.changeDetected = true;
      }
      // Só carregar locations quando há providers selecionados e o combo está visível
      const hasProviders = this.provChoice && (Array.isArray(this.provChoice) ? this.provChoice.length > 0 : !!this.provChoice);
      if (hasProviders && !this.locDisabled) {
        this.getLocations();
      } else {
        this.locations = [];
      }
    },
    locChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    startDate() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    endDate() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
    reportChoice() {
      if (this.showReport) {
        this.changeDetected = true;
      }
    },
  },
  computed: {
    userRoleLower() {
      return (this.user && this.user.role && String(this.user.role).toLowerCase()) || '';
    },
    visibleReports() {
      // Para Provider do FamilyHealthChoice, remover "Patients Not Seen"
      const isFHCProvider = (this.user.role === 'Provider' || this.user.role === 'Specialist') && 
                            this.user.insurance && 
                            this.user.insurance.some(ins => ins === 'FamilyHealthChoice');
      
      if (isFHCProvider) {
        return this.defaultReports.filter(r => r !== 'Patients Not Seen');
      }
      
      if (this.user.role === 'Admin' || this.user.role === 'Superuser' || this.user.role === 'Manager') {
        return this.defaultReports;
      }
      return this.defaultReports;
    },
    orgDisabled() {
      return false;
    },
    insDisabled() {
      return false;
    },
    provDisabled() {
      // if (this.reportChoice === 'Membership Summary') {
      //   return true;
      // }
      return false;
    },
    locDisabled() {
      if (
        this.provChoice.length === 0
        || this.groupChoice.length === 0
        || this.reportChoice === 'Capitation'
        || this.reportChoice === 'Membership Summary'
      ) {
        return true;
      }
      return false;
    },
    groupDisabled() {
      // if (this.reportChoice === 'Membership Summary' && this.user.role === 'Admin') {
      //   this.groupChoice = [];
      //   return true;
      // }
      if (this.user.role === 'Provider') {
        return true;
      }
      // if (this.user.role === 'Group' && this.groups.length === 1) {
      //   this.groupChoice = this.user.group;
      //   return true;
      // }
      return false;
    },
    endDateDisabled() {
      if (this.reportChoice !== 'Membership Summary') {
        return true;
      }
      return false;
    },
    startDateText() {
      if (this.reportChoice !== 'Membership Summary') {
        return 'Month';
      }
      return 'Start Date';
    },
  },
};
</script>
<style scoped>
.newSelect .v-select__selection {
  white-space: nowrap;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selection-text {
  margin-right: 5px;
}
@media only screen and (min-width: 1200px) {
  .selection-text {
    white-space: nowrap;
    overflow: hidden;
    width: 70%;
  }
}
</style>
